import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { BASE_API_URL } from '@api';
import QuickHelpContext from './context';
import type {
    QuickHelpMessage,
    QuickHelpState,
    QuickHelpTab,
    WidgetContext,
} from './types';

const PANEL_WIDTH = 400;
const MAX_TABS = 6;

// ── Storage helpers ──────────────────────────────────────────────────────────

const QH_TABS_KEY = 'quick_help_tabs';
const QH_ACTIVE_KEY = 'quick_help_active';
const QH_MSG_PREFIX = 'quick_help_msg';

function getOrgId(): string | null {
    try {
        const u = localStorage.getItem('user');
        if (u) {
            const parsed = JSON.parse(u) as { organizationId?: string };
            return parsed.organizationId ?? null;
        }
    } catch { /* */ }
    return null;
}

function scopedKey(key: string): string {
    const orgId = getOrgId();
    return orgId ? `${key}_${orgId}` : key;
}

type StoredTabMeta = { id: string; threadId: string | null; title: string };
type StoredMessage = { id: string; role: 'user' | 'assistant'; content: string; createdAt: string };

function loadStoredTabs(): StoredTabMeta[] {
    try {
        const raw = sessionStorage.getItem(scopedKey(QH_TABS_KEY));
        return raw ? (JSON.parse(raw) as StoredTabMeta[]) : [];
    } catch { return []; }
}

function saveStoredTabs(tabs: StoredTabMeta[]): void {
    try {
        sessionStorage.setItem(scopedKey(QH_TABS_KEY), JSON.stringify(tabs));
    } catch { /* */ }
}

function loadStoredActiveTabId(): string | null {
    try {
        return sessionStorage.getItem(scopedKey(QH_ACTIVE_KEY));
    } catch { return null; }
}

function saveStoredActiveTabId(id: string | null): void {
    try {
        if (id) sessionStorage.setItem(scopedKey(QH_ACTIVE_KEY), id);
        else sessionStorage.removeItem(scopedKey(QH_ACTIVE_KEY));
    } catch { /* */ }
}

function loadTabMessages(tabId: string): QuickHelpMessage[] {
    try {
        const raw = sessionStorage.getItem(scopedKey(`${QH_MSG_PREFIX}_${tabId}`));
        if (!raw) return [];
        const stored = JSON.parse(raw) as StoredMessage[];
        return stored.map((m) => ({ ...m, createdAt: new Date(m.createdAt), isStreaming: false }));
    } catch { return []; }
}

function saveTabMessages(tabId: string, messages: QuickHelpMessage[]): void {
    try {
        const toSave: StoredMessage[] = messages
            .filter((m) => !m.isStreaming)
            .map((m) => ({
                id: m.id,
                role: m.role,
                content: m.content,
                createdAt: m.createdAt instanceof Date ? m.createdAt.toISOString() : String(m.createdAt),
            }));
        sessionStorage.setItem(scopedKey(`${QH_MSG_PREFIX}_${tabId}`), JSON.stringify(toSave));
    } catch { /* */ }
}

function clearTabStorage(tabId: string): void {
    try {
        sessionStorage.removeItem(scopedKey(`${QH_MSG_PREFIX}_${tabId}`));
    } catch { /* */ }
}

/** Call this on logout to wipe all quick-help session data. */
export function clearQuickHelpSessionStorage(): void {
    try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith('quick_help_')) keysToRemove.push(key);
        }
        for (const key of keysToRemove) sessionStorage.removeItem(key);
    } catch { /* */ }
}

// ── Tab factory ──────────────────────────────────────────────────────────────

function makeTab(context?: WidgetContext): QuickHelpTab {
    return {
        id: uuidv4(),
        threadId: null,
        title: 'New Chat',
        messages: [],
        attachedContext: context ?? null,
        dashboardContext: context?.page ?? null,
        isStreaming: false,
    };
}

// ── Initial state (hydrated from sessionStorage) ─────────────────────────────

function buildInitialState(): QuickHelpState {
    const storedMeta = loadStoredTabs();
    const storedActiveId = loadStoredActiveTabId();

    const tabs: QuickHelpTab[] = storedMeta.map((meta) => ({
        id: meta.id,
        threadId: meta.threadId,
        title: meta.title,
        messages: loadTabMessages(meta.id),
        attachedContext: null,
        dashboardContext: null,
        isStreaming: false,
    }));

    const activeTabId =
        tabs.find((t) => t.id === storedActiveId) ? storedActiveId : (tabs[0]?.id ?? null);

    return { isOpen: false, tabs, activeTabId, pendingContext: null };
}

// ── Provider ─────────────────────────────────────────────────────────────────

const QuickHelpProvider: FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const [state, setState] = useState<QuickHelpState>(buildInitialState);
    const abortControllerRef = useRef<Map<string, AbortController>>(new Map());

    // Persist tab metadata + activeTabId on every change
    useEffect(() => {
        saveStoredTabs(state.tabs.map((t) => ({ id: t.id, threadId: t.threadId, title: t.title })));
    }, [state.tabs]);

    useEffect(() => {
        saveStoredActiveTabId(state.activeTabId);
    }, [state.activeTabId]);

    // Persist messages per tab (skip mid-stream to avoid partial content)
    useEffect(() => {
        for (const tab of state.tabs) {
            if (!tab.isStreaming && tab.messages.length > 0) {
                saveTabMessages(tab.id, tab.messages);
            }
        }
    }, [state.tabs]);

    // ── Panel open/close ─────────────────────────────────────────────────────

    const openPanel = useCallback(() => {
        setState((prev) => {
            const next = { ...prev, isOpen: true };
            if (next.tabs.length === 0) {
                const tab = makeTab(prev.pendingContext ?? undefined);
                next.tabs = [tab];
                next.activeTabId = tab.id;
                next.pendingContext = null;
            }
            return next;
        });
    }, []);

    const closePanel = useCallback(() => {
        setState((prev) => ({ ...prev, isOpen: false }));
    }, []);

    const togglePanel = useCallback(() => {
        setState((prev) => {
            const next = { ...prev, isOpen: !prev.isOpen };
            if (next.isOpen && next.tabs.length === 0) {
                const tab = makeTab(prev.pendingContext ?? undefined);
                next.tabs = [tab];
                next.activeTabId = tab.id;
                next.pendingContext = null;
            }
            return next;
        });
    }, []);

    // ── Tab management ───────────────────────────────────────────────────────

    const createTab = useCallback((context?: WidgetContext): string => {
        let tabId = '';
        setState((prev) => {
            const tab = makeTab(context);
            tabId = tab.id;
            if (prev.tabs.length >= MAX_TABS) {
                clearTabStorage(prev.tabs[0].id);
            }
            const tabs =
                prev.tabs.length >= MAX_TABS
                    ? [...prev.tabs.slice(1), tab]
                    : [...prev.tabs, tab];
            return { ...prev, tabs, activeTabId: tab.id, isOpen: true };
        });
        return tabId;
    }, []);

    const closeTab = useCallback((tabId: string) => {
        abortControllerRef.current.get(tabId)?.abort();
        abortControllerRef.current.delete(tabId);
        clearTabStorage(tabId);
        setState((prev) => {
            const tabs = prev.tabs.filter((t) => t.id !== tabId);
            const activeTabId =
                prev.activeTabId === tabId
                    ? (tabs[tabs.length - 1]?.id ?? null)
                    : prev.activeTabId;
            return { ...prev, tabs, activeTabId };
        });
    }, []);

    const setActiveTab = useCallback((tabId: string) => {
        setState((prev) => ({ ...prev, activeTabId: tabId }));
    }, []);

    const updateTabTitle = useCallback((tabId: string, title: string) => {
        setState((prev) => ({
            ...prev,
            tabs: prev.tabs.map((t) => (t.id === tabId ? { ...t, title } : t)),
        }));
    }, []);

    // ── Context staging ──────────────────────────────────────────────────────

    const stagePendingContext = useCallback((context: WidgetContext) => {
        setState((prev) => ({ ...prev, pendingContext: context }));
    }, []);

    const clearPendingContext = useCallback(() => {
        setState((prev) => ({ ...prev, pendingContext: null }));
    }, []);

    // ── Messaging ────────────────────────────────────────────────────────────

    const sendMessage = useCallback(
        async (tabId: string, message: string) => {
            abortControllerRef.current.get(tabId)?.abort();
            const abortController = new AbortController();
            abortControllerRef.current.set(tabId, abortController);

            const tab = state.tabs.find((t) => t.id === tabId);
            if (!tab) return;

            const userMessageId = uuidv4();
            const assistantMessageId = uuidv4();

            const userMsg: QuickHelpMessage = {
                id: userMessageId,
                role: 'user',
                content: message,
                createdAt: new Date(),
            };

            const assistantMsg: QuickHelpMessage = {
                id: assistantMessageId,
                role: 'assistant',
                content: '',
                createdAt: new Date(),
                isStreaming: true,
            };

            setState((prev) => ({
                ...prev,
                tabs: prev.tabs.map((t) =>
                    t.id === tabId
                        ? { ...t, messages: [...t.messages, userMsg, assistantMsg], isStreaming: true }
                        : t,
                ),
            }));

            try {
                const token = localStorage.getItem('token');
                const response = await fetch(
                    `${BASE_API_URL}/api/v1/agent/quick-help/chat`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        },
                        body: JSON.stringify({
                            message,
                            thread_id: tab.threadId ?? undefined,
                            dashboard_context: tab.dashboardContext ?? undefined,
                            widget_context: tab.attachedContext
                                ? {
                                      component_name: tab.attachedContext.component_name,
                                      page: tab.attachedContext.page,
                                      request_payload: tab.attachedContext.request_payload,
                                  }
                                : undefined,
                        }),
                        signal: abortController.signal,
                    },
                );

                if (!response.ok || !response.body) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    const { value, done } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() ?? '';

                    for (const line of lines) {
                        if (!line.startsWith('data: ')) continue;
                        const data = line.slice(6).trim();
                        if (!data) continue;

                        try {
                            const parsed = JSON.parse(data) as {
                                type: string;
                                threadId?: string;
                                content?: string;
                            };

                            if (parsed.type === 'thread' && parsed.threadId) {
                                setState((prev) => ({
                                    ...prev,
                                    tabs: prev.tabs.map((t) =>
                                        t.id === tabId
                                            ? {
                                                  ...t,
                                                  threadId: parsed.threadId!,
                                                  title:
                                                      t.title === 'New Chat'
                                                          ? message.slice(0, 40) +
                                                            (message.length > 40 ? '…' : '')
                                                          : t.title,
                                              }
                                            : t,
                                    ),
                                }));
                            }

                            if (parsed.type === 'status' && parsed.content) {
                                setState((prev) => ({
                                    ...prev,
                                    tabs: prev.tabs.map((t) =>
                                        t.id === tabId
                                            ? { ...t, statusText: parsed.content }
                                            : t,
                                    ),
                                }));
                            }

                            if (parsed.type === 'text-delta' && parsed.content) {
                                setState((prev) => ({
                                    ...prev,
                                    tabs: prev.tabs.map((t) =>
                                        t.id === tabId
                                            ? {
                                                  ...t,
                                                  statusText: undefined,
                                                  messages: t.messages.map((m) =>
                                                      m.id === assistantMessageId
                                                          ? { ...m, content: m.content + parsed.content }
                                                          : m,
                                                  ),
                                              }
                                            : t,
                                    ),
                                }));
                            }
                        } catch {
                            // Skip malformed chunks
                        }
                    }
                }
            } catch (error) {
                if (error instanceof Error && error.name === 'AbortError') return;

                setState((prev) => ({
                    ...prev,
                    tabs: prev.tabs.map((t) =>
                        t.id === tabId
                            ? {
                                  ...t,
                                  messages: t.messages.map((m) =>
                                      m.id === assistantMessageId
                                          ? { ...m, content: 'Something went wrong. Please try again.', isStreaming: false }
                                          : m,
                                  ),
                              }
                            : t,
                    ),
                }));
            } finally {
                abortControllerRef.current.delete(tabId);
                setState((prev) => ({
                    ...prev,
                    tabs: prev.tabs.map((t) =>
                        t.id === tabId
                            ? {
                                  ...t,
                                  isStreaming: false,
                                  statusText: undefined,
                                  attachedContext: null,
                                  messages: t.messages.map((m) =>
                                      m.id === assistantMessageId
                                          ? {
                                                ...m,
                                                isStreaming: false,
                                                content: m.content || "I wasn't able to get an answer for that. Please try rephrasing your question.",
                                            }
                                          : m,
                                  ),
                              }
                            : t,
                    ),
                }));
            }
        },
        [state.tabs],
    );

    const actions = useMemo(
        () => ({
            openPanel,
            closePanel,
            togglePanel,
            createTab,
            closeTab,
            setActiveTab,
            sendMessage,
            stagePendingContext,
            clearPendingContext,
            updateTabTitle,
        }),
        [
            openPanel,
            closePanel,
            togglePanel,
            createTab,
            closeTab,
            setActiveTab,
            sendMessage,
            stagePendingContext,
            clearPendingContext,
            updateTabTitle,
        ],
    );

    const value = useMemo(() => ({ state, actions }), [state, actions]);

    return (
        <QuickHelpContext.Provider value={value}>
            {children}
        </QuickHelpContext.Provider>
    );
};

export default QuickHelpProvider;
export { PANEL_WIDTH };
